import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAdminBffClient } from './admin-bff-client';
import type {
  AdminStats,
  AdminAiService,
  AdminAiServiceVersion,
  AdminOrganization,
  AdminExtractionStep,
  AdminPathFilter,
  PlatformAdmin,
  ExtractionTestResult,
} from '../types/admin-types';

// Dashboard

export function useAdminStats() {
  const client = useAdminBffClient();
  return useQuery({
    queryKey: ['admin', 'stats'],
    queryFn: () => client.get<AdminStats>('/bff/admin/stats'),
  });
}

// AI Services

export function useAdminAiServices() {
  const client = useAdminBffClient();
  return useQuery({
    queryKey: ['admin', 'ai-services'],
    queryFn: () => client.get<AdminAiService[]>('/bff/admin/ai-services'),
  });
}

export function useAdminAiService(id: string) {
  const client = useAdminBffClient();
  return useQuery({
    queryKey: ['admin', 'ai-services', id],
    queryFn: () => client.get<AdminAiService>(`/bff/admin/ai-services/${id}`),
    enabled: !!id,
  });
}

export function useUpdateAdminAiService() {
  const client = useAdminBffClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; name: string; provider: string | null; category: string; isEnabled: boolean }) =>
      client.put<AdminAiService>(`/bff/admin/ai-services/${id}`, body),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'ai-services'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'ai-services', variables.id] });
    },
  });
}

export function useReplaceExtractionRules() {
  const client = useAdminBffClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, steps }: { id: string; steps: AdminExtractionStep[] }) =>
      client.post(`/bff/admin/ai-services/${id}/extraction-rules`, { steps }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'ai-services', variables.id] });
    },
  });
}

export function useReplacePathFilters() {
  const client = useAdminBffClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, filters }: { id: string; filters: AdminPathFilter[] }) =>
      client.post(`/bff/admin/ai-services/${id}/path-filters`, { filters }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'ai-services', variables.id] });
    },
  });
}

export function useAddAiServiceDomain() {
  const client = useAdminBffClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, domain }: { id: string; domain: string }) =>
      client.post(`/bff/admin/ai-services/${id}/domains`, { domain }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'ai-services', variables.id] });
    },
  });
}

export function useRemoveAiServiceDomain() {
  const client = useAdminBffClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, domainId }: { id: string; domainId: string }) =>
      client.delete(`/bff/admin/ai-services/${id}/domains/${domainId}`),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'ai-services', variables.id] });
    },
  });
}

export function useTestExtraction() {
  const client = useAdminBffClient();
  return useMutation({
    mutationFn: ({ body, steps }: { body: string; steps: AdminExtractionStep[] }) =>
      client.post<ExtractionTestResult>('/bff/admin/ai-services/test-extraction', { body, steps }),
  });
}

export function useToggleAiService() {
  const client = useAdminBffClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      client.post(`/bff/admin/ai-services/${id}/toggle`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'ai-services'] });
    },
  });
}

// AI Service Versions

export function useAiServiceVersions(id: string) {
  const client = useAdminBffClient();
  return useQuery({
    queryKey: ['admin', 'ai-service-versions', id],
    queryFn: () => client.get<AdminAiServiceVersion[]>(`/bff/admin/ai-services/${id}/versions`),
    enabled: !!id,
  });
}

export function useCreateAiServiceVersion() {
  const client = useAdminBffClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, steps, filters }: { id: string; steps: AdminExtractionStep[]; filters: AdminPathFilter[] }) =>
      client.post<{ versionId: number }>(`/bff/admin/ai-services/${id}/versions`, { steps, filters }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'ai-services', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'ai-service-versions', variables.id] });
    },
  });
}

export function usePublishAiServiceVersion() {
  const client = useAdminBffClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, versionId }: { id: string; versionId: number }) =>
      client.post(`/bff/admin/ai-services/${id}/versions/${versionId}/publish`, {}),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'ai-services', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'ai-service-versions', variables.id] });
    },
  });
}

// Organizations

export function useAdminOrganizations() {
  const client = useAdminBffClient();
  return useQuery({
    queryKey: ['admin', 'organizations'],
    queryFn: () => client.get<AdminOrganization[]>('/bff/admin/organizations'),
  });
}

export function useApproveOrganization() {
  const client = useAdminBffClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      client.post(`/bff/admin/organizations/${id}/approve`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'organizations'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
    },
  });
}

export function useRejectOrganization() {
  const client = useAdminBffClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      client.post(`/bff/admin/organizations/${id}/reject`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'organizations'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
    },
  });
}

export function useSuspendOrganization() {
  const client = useAdminBffClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      client.post(`/bff/admin/organizations/${id}/suspend`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'organizations'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
    },
  });
}

export function useActivateOrganization() {
  const client = useAdminBffClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      client.post(`/bff/admin/organizations/${id}/activate`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'organizations'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
    },
  });
}

// Platform Admins

export function useAdminPlatformAdmins() {
  const client = useAdminBffClient();
  return useQuery({
    queryKey: ['admin', 'admins'],
    queryFn: () => client.get<PlatformAdmin[]>('/bff/admin/admins'),
  });
}

export function useGrantPlatformAdmin() {
  const client = useAdminBffClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ email }: { email: string }) =>
      client.post('/bff/admin/admins', { email }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'admins'] });
    },
  });
}

export function useRevokePlatformAdmin() {
  const client = useAdminBffClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      client.delete(`/bff/admin/admins/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'admins'] });
    },
  });
}
